import toolsConfig from 'pages/Batch/configs/toolsConfig';
import Toolchain from 'pages/Batch/utils/process/toolchain';
import { BatchDataStatus } from 'configs/constants';
import type { IBatchData } from 'types/store';
import { BATCH_ACTIONS } from 'pages/Batch/configs';
import analytics, { getToolAnalyticsName } from 'pages/Batch/utils/analytics';

import type { IActionsPreset, IPresetReplayResult, IPresetStep } from './types';
import { createEmptyPresetReplayResult } from './replayResult';
import { persistItemsToService } from './persistResults';
import { deserializeStepParams } from './paramSerializer';
import { buildPresetSubToolsByTool } from './presetSubToolsBuilder';

/** Actions that fetch images by key from BatchDataService instead of using the passed HTMLImageElement */
const SERVER_SIDE_ACTIONS: ReadonlySet<BATCH_ACTIONS> = new Set([BATCH_ACTIONS.removeBackground, BATCH_ACTIONS.aiEnhance, BATCH_ACTIONS.generateAIBackground, BATCH_ACTIONS.aiEdit]);

interface IPipelineState {
  result: IPresetReplayResult;
  runningData: IBatchData[];
}

async function executeStep(state: IPipelineState, step: IPresetStep, nextStep?: IPresetStep): Promise<IPipelineState> {
  const { result, runningData } = state;

  try {
    const { attrs, skip } = await deserializeStepParams(step, runningData);

    if (skip) {
      result.skippedSteps.push({
        action: step.action,
        success: false,
        skipped: true,
        skipReason: skip,
      });
      return state;
    }

    const { getProcess, usePending } = toolsConfig[step.action];
    const processFn = getProcess(attrs);
    const toolchain = new Toolchain();

    // Collect processed items
    const processedMap = new Map<string, IBatchData>();

    await toolchain.process({
      callback: (processedItem: IBatchData) => {
        processedMap.set(processedItem.key, processedItem);
      },
      usePending,
      processFn,
      srcArray: runningData,
    });

    // Commit: promote pendingResult → result so the next step sees this step's output
    const committedData = runningData.map(item => {
      const processed = processedMap.get(item.key);
      if (!processed) return item;

      if (processed.status === BatchDataStatus.ERROR) {
        return { ...processed };
      }

      return {
        ...processed,
        result: processed.pendingResult || processed.result,
        pendingResult: undefined,
        isProcessed: true,
      };
    });

    // Persist intermediate results if the next step fetches images from BatchDataService
    if (nextStep && SERVER_SIDE_ACTIONS.has(nextStep.action)) {
      await persistItemsToService(committedData);
    }

    result.completedSteps++;
    return { result, runningData: committedData };
  } catch (error) {
    result.errors.push({
      action: step.action,
      success: false,
      skipReason: error instanceof Error ? error.message : 'unknown_error',
    });
    return state;
  }
}

export async function replayActionPreset(preset: IActionsPreset, selectedData: IBatchData[]): Promise<IPresetReplayResult> {
  const initial: IPipelineState = {
    result: createEmptyPresetReplayResult(),
    runningData: selectedData.map(item => ({ ...item })),
  };

  const finalState = await preset.steps.reduce(async (accPromise, step, index) => executeStep(await accPromise, step, preset.steps[index + 1]), Promise.resolve(initial));

  finalState.result.processedData = finalState.runningData;

  if (selectedData.length > 0) {
    const subToolsByTool = buildPresetSubToolsByTool(preset.steps);
    subToolsByTool.forEach((subTools, tool) => {
      analytics.presetApply(getToolAnalyticsName(tool), selectedData.length, subTools);
    });
  }

  return finalState.result;
}
